import { readableInk } from './contrastInk.js';

// Product colour helpers — the "real" colour of a finished cable so it's visible at
// a glance in the Project / Bobin-State tables. For sheathed cables (MYYM/CYYF/CYABYF)
// this is the sheath colour (WHITE/BLACK/GREY…); for single-conductor cables (FY/MYF)
// it's the wire colour. Both are encoded as the colour segment of the launch
// productKey ("MYYM|3|2.5|0||WHITE" → WHITE) / Standard.color.

export const CABLE_COLOR_HEX = {
  BLACK: '#1a1a1a',
  BLUE: '#2f7cff',
  BROWN: '#8b5a2b',
  'GREEN-YELLOW': '#9acd32',
  GREEN: '#2e8b57',
  GREY: '#7a7a7a',
  GRAY: '#7a7a7a',
  'GREY 7004': '#6b7280',
  RED: '#d9534f',
  YELLOW: '#f0c400',
  ORANGE: '#f39c12',
  WHITE: '#e8e8e8',
  NATUR: '#efe6d4'
};

const KNOWN = Object.keys(CABLE_COLOR_HEX);

export function colorNameFromKey(productKey) {
  const parts = String(productKey || '').split('|');
  return (
    parts.find(seg => KNOWN.includes(String(seg).trim().toUpperCase())) || ''
  ).toUpperCase();
}

// Derive the PRODUCT (sheath / wire) colour name from a job / bobin row. Uses ONLY
// product-level sources — the launch productKey (jobs) or the `productColor` DTO field
// (bobin rows). Deliberately does NOT fall back to `row.color`, which on a bobin row is
// the CONDUCTOR/token colour (BLUE, BROWN…), not the finished-product colour.
export function productColorName(row) {
  if (!row) return '';
  return (
    colorNameFromKey(row.source?.launch?.productKey) ||
    (row.productColor ? String(row.productColor).trim().toUpperCase() : '')
  );
}

export function productColorHex(name) {
  const c = String(name || '').trim().toUpperCase();
  return CABLE_COLOR_HEX[c] || null;
}

export function productColorBackground(name) {
  const c = String(name || '').trim().toUpperCase();
  if (c === 'GREEN-YELLOW')
    return 'linear-gradient(135deg,#2e7d32 50%,#ffd600 50%)';
  return CABLE_COLOR_HEX[c] || '#8892a0';
}

/**
 * A chip painted in the cable's own colour, with the colour's name on it.
 *
 * The dot says a colour exists; a table full of dots still has to be hovered
 * one at a time to find the white MYYM among the greys. The name on the chip is
 * readable at a glance and searchable by eye.
 *
 * The ink is measured, not assumed — WHITE and NATUR are near-white and need
 * dark text, BLACK needs light, and green-yellow is a two-colour ground whose
 * flat equivalent is light. Guessing gets those wrong in both directions.
 */
export function productColorBadgeStyle(name) {
  const c = String(name || '').trim().toUpperCase();
  if (!c) return null;

  return {
    background: productColorBackground(c),
    // The gradient has no single hex to measure, so the ink is judged on the
    // flat equivalent of green-yellow, which is light enough for dark text.
    color: readableInk(productColorHex(c) || '#8892a0'),
    // WHITE and NATUR are all but invisible against a light card without it.
    border: '1px solid rgba(120,120,120,0.55)'
  };
}
